//FOR Quotation state management
import { useSnackBar } from '../use-snackbar';
import useQuotationApi from './use-quotation-api';
import { CheckAvailabilityOption } from 'src/redux/slices/quotation-v2/type';
import { useLocation } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { NewTemplateDetails, Quotation } from 'src/types';
import { initialQuotationState } from 'src/constants/quotation/index';
import useUserInfo from '../user/use-user-info';
import {
  isQuotationPCBuild,
  quotationProductTemplateFormater,
  itemsOrganizer,
  timeStampNow,
  transformEcommQuotationLink
} from 'src/utils';
import QRCode from 'qrcode';
import { copyToClipboard } from 'src/utils';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';

const {
  selectors: quotationSelectors,
  actions: quotationActions
} = slices.quotationV2;

const useQuotation = () => {
  const snackBar = useSnackBar();
  const location: any = useLocation();
  const dispatch = useAppDispatch();
  const { checkQuotationAvailability, createTemplate } = useQuotationApi();
  const { userBranchIdDefaultOption, favoriteBranch } = useUserInfo();
  const quotationSelectedBranch = useAppSelector(
    quotationSelectors.selectQuotationBranchId
  );

  const [quotationFromStateLocation, setQuotationFromStateLocation] = useState<
    Quotation[]
  >(location.state);
  const [items, setItems] = useState<Quotation[]>(
    quotationFromStateLocation || initialQuotationState || []
  );
  const [selectedBranchID, setSelectedBranchID] = useState<number | undefined>(
    favoriteBranch ?? userBranchIdDefaultOption
  );
  const [isBoost, setIsBoost] = useState<number>(0);
  const [walkinUriQR, setWalkInUriQR] = useState<string>('');
  const [walkInFullLink, setWalkInFullLink] = useState<string>('');
  const [ecommLink, setEcommLink] = useState<string>('');
  const [disable, setDisable] = useState<boolean>(true);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedVoucherCode, setSelectedVoucherCode] = useState<string>('');

  const selectQuotationBranchId = async (branch_id: number) => {
    await dispatch(quotationActions.setQuotationBranchID(branch_id));
  };

  const clearQuotationBranch = async () => {
    await dispatch(quotationActions.setQuotationClearBranch());
  };

  const onCheckAvailability = async (
    checkAvailabilityOption?: CheckAvailabilityOption,
    quotationData?: Quotation[]
  ) => {
    const newItems = itemsOrganizer(quotationData || []);

    const checkerRes = await checkQuotationAvailability({
      items: newItems,
      branch_id: quotationSelectedBranch || userBranchIdDefaultOption
    });

    if (checkerRes?.success) {
      const resItems = checkerRes?.originalData?.quote_items || [];
      const hasWarning = resItems?.filter((x) => x.warning_note);

      if (hasWarning?.length) {
        snackBar.show({
          severity: 'error',
          message:
            'Some item(s) might be out of stock. Please check availability.'
        });
      } else {
        snackBar.show({
          severity: 'success',
          message: 'All items available'
        });
      }
      setItems(resItems);

      // If we are waiting for response from another function
      if (checkAvailabilityOption?.returnItems) {
        return resItems;
      }

      return;
    }

    snackBar.show({
      severity: 'error',
      message: 'Error checking availability. Please try again later.'
    });
    setItems(newItems);
  };

  const onGenerateAddToCartLink = async (quotationData: Quotation[]) => {
    // Fix structure of quotation and check availability of items
    const newArrangedItems = await onCheckAvailability(
      { returnItems: true },
      quotationData
    );

    if (!newArrangedItems || newArrangedItems?.length <= 0) {
      snackBar.show({
        severity: 'error',
        message: 'Error structuring quotation and checking of availability',
        useSound: true
      });
      return;
    }

    // Clear quotation that is passed from state?
    setQuotationFromStateLocation([]);

    const newTemplateProducts = quotationProductTemplateFormater(
      newArrangedItems
    );

    const isNewTemplatePCBuild = isQuotationPCBuild(newTemplateProducts);

    const finalNewTemplate: NewTemplateDetails = {
      products: newTemplateProducts,
      name: `AddToCartLink-${timeStampNow()}`,
      pc_build: isNewTemplatePCBuild ? 1 : 0,
      boost: isBoost
    };

    const params = {
      ...finalNewTemplate,
      branch_id: quotationSelectedBranch
    };

    // Finally save new template
    const createQuoteResponse = await createTemplate(params);

    clearQuotationBranch();

    return createQuoteResponse;
  };

  const getTransactionNoUrl = useCallback(() => {
    try {
      if (walkInFullLink) {
        QRCode.toDataURL(walkInFullLink, { errorCorrectionLevel: 'H' })
          .then((url) => setWalkInUriQR(url))
          .catch((err) => console.error(err));
      }
    } catch (error) {
      console.error(error);
    }
  }, [walkInFullLink]);

  const onGenerateLink = async (quotationData: Quotation[]) => {
    // TODO: ADD LOADER. Clear qr code and link immediately even before generating link
    setWalkInUriQR('');
    setWalkInFullLink('');
    setEcommLink('');

    const generateResponse = await onGenerateAddToCartLink(quotationData);

    const addToCartLink = generateResponse?.originalData?.add_to_user_cart_link;
    if (addToCartLink) {
      setWalkInFullLink(addToCartLink);
      setEcommLink(transformEcommQuotationLink(addToCartLink));
      snackBar.show({
        severity: 'success',
        message: `Please ensure that the customer already has account on pcworth.com before sending the link. Link generated successfully. Link copied to clipboard`,
        useSound: true
      });
      copyToClipboard(addToCartLink);
      setOpenDialog(true);
      return;
    }

    setWalkInFullLink('');
    setEcommLink('');
    snackBar.show({
      severity: 'error',
      message: `There was an error generating the add to cart link`,
      useSound: true
    });
  };

  useEffect(getTransactionNoUrl, [getTransactionNoUrl]);

  useEffect(() => {
    if (quotationSelectedBranch === undefined) {
      selectQuotationBranchId(favoriteBranch || userBranchIdDefaultOption || 0);
    }
  }, [
    favoriteBranch,
    quotationSelectedBranch,
    selectQuotationBranchId,
    userBranchIdDefaultOption
  ]);

  return {
    //state
    items,
    setItems,
    selectedBranchID,
    setSelectedBranchID,
    isBoost,
    setIsBoost,
    walkinUriQR,
    walkInFullLink,
    setWalkInFullLink,
    ecommLink,
    setEcommLink,
    disable,
    setDisable,
    openDialog,
    setOpenDialog,
    selectedVoucherCode,
    setSelectedVoucherCode,

    //functions
    itemsOrganizer,
    onCheckAvailability,
    onGenerateAddToCartLink,
    onGenerateLink,
    selectQuotationBranchId
  };
};

export default useQuotation;
